import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';

import { renderReportPreview } from '../../library/fr3render';
import {
    buildPreviewInput,
    fetchRunDatasets,
    runReportPipeline,
    splitRecIdList,
    validateRecIds,
} from './reportPipeline';
import { RecIdInput } from './ReportVariableInputs';
import { recIdLookupKind } from './recIdLabels';

/**
 * Document preview: run a report for one or more document ids (or reuse a
 * finished run), fetch its .fr3 layout(s) and logo, and render a PDF with
 * the browser fr3 engine — the same layouts and data the tablet prints.
 *
 * Props:
 *   open, onClose, api, sftUserId, t, lang, theme
 *   repid, title
 *   documentKind  'invoice' | 'ticket' (logo choice: invoice → invoice logo)
 *   recIdSlots    [{ slot, label }] asked when no runId is given
 *   runId         optional finished run of the reports module
 *   storeId, posId optional scope (logo cascade + {WAREHOUSEID}/{POSID})
 *   templateOverride optional { name, bytes }: render this layout instead of
 *                  the stored file of that name (the designer's unsaved edit)
 *   report         optional report row (fetchReports): RecId labels and pick
 *                  lists from its recid_fields, like the run dialog
 *   lookups        optional fetchReports lookups (corps / stores / registers)
 *   variables      optional extra header variables (e.g. RepSelection)
 */
const ReportPreviewDialog = (props) => {
    const { open, onClose } = props;
    const t = props.t || ((k, d) => d || k);
    const tx = (key, fallback) => {
        const value = t(key);
        return value && value !== key ? value : fallback;
    };

    // ask | running | rendering | done | unavailable | error
    const [phase, setPhase] = useState('ask');
    const [values, setValues] = useState({});
    const [progress, setProgress] = useState(null);
    const [message, setMessage] = useState('');
    const [input, setInput] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [warnings, setWarnings] = useState([]);
    const cancelled = useRef(false);

    const slots = (props.recIdSlots || []).map((entry) => entry.slot);

    useEffect(() => {
        if (!open) {
            cancelled.current = true;
            setPhase('ask');
            setValues({});
            setProgress(null);
            setMessage('');
            setInput(null);
            setWarnings([]);
            setPdfUrl((current) => {
                if (current) URL.revokeObjectURL(current);
                return '';
            });
            return;
        }
        cancelled.current = false;
        if (props.runId) {
            renderFromRun(props.runId);
        } else if (props.report && props.lookups) {
            // A firm pick list with one firm: preselect it (as the run dialog).
            const preset = {};
            slots.forEach((slot) => {
                const kind = recIdLookupKind(props.report, slot);
                const list = kind && Array.isArray(props.lookups[kind]) ? props.lookups[kind] : [];
                if (kind === 'corps' && list.length === 1) preset[slot] = list[0].value;
            });
            setValues(preset);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, props.runId]);

    const render = async (status) => {
        setPhase('rendering');
        const built = await buildPreviewInput({
            api: props.api,
            status,
            documentKind: props.documentKind || 'ticket',
            storeId: props.storeId || '',
            posId: props.posId || '',
            title: props.title || '',
            lang: props.lang || '',
            variables: props.variables || null,
        });
        if (cancelled.current) return;
        const override = props.templateOverride;
        if (override && override.name && override.bytes) {
            const name = String(override.name).toLowerCase();
            built.datasets.forEach((ds) => {
                if (String(ds.template || '').toLowerCase() === name) {
                    ds.templateBytes = override.bytes;
                    ds.templateXml = null;
                    ds.skipped = null;
                    ds.error = undefined;
                }
            });
        }
        setInput(built);
        try {
            const result = await renderReportPreview(built);
            if (cancelled.current) return;
            const url = URL.createObjectURL(new Blob([result.pdf], { type: 'application/pdf' }));
            setPdfUrl(url);
            setWarnings(result.warnings || []);
            setPhase('done');
        } catch (error) {
            if (cancelled.current) return;
            if (error && error.notAvailable) {
                setPhase('unavailable');
            } else {
                setMessage(error?.message || 'Render failed');
                setPhase('error');
            }
        }
    };

    const renderFromRun = async (runId) => {
        try {
            setPhase('running');
            const status = await fetchRunDatasets({ api: props.api, runId });
            if (cancelled.current) return;
            await render(status);
        } catch (error) {
            if (cancelled.current) return;
            setMessage(error?.message || 'Error');
            setPhase('error');
        }
    };

    const start = async () => {
        const recids = {};
        slots.forEach((slot) => { recids[slot] = splitRecIdList(values[slot]).join(','); });
        try {
            setPhase('running');
            const status = await runReportPipeline({
                api: props.api,
                sftUserId: props.sftUserId,
                repid: props.repid,
                recids,
                storeId: props.storeId || '',
                posId: props.posId || '',
                lang: props.lang || '',
                withDatasets: true,
                onProgress: (data) => setProgress(data),
                isCancelled: () => cancelled.current,
            });
            if (cancelled.current) return;
            await render(status);
        } catch (error) {
            if (cancelled.current || error?.message === 'cancelled') return;
            setMessage(error?.message || 'Error');
            setPhase('error');
        }
    };

    const validation = validateRecIds(slots, values);

    const skippedLabel = (reason) => {
        switch (reason) {
            case 'frf': return tx('rpv_skip_frf', 'FastReport 2 (.frf) — incompatible');
            case 'no_layout': return tx('rpv_skip_no_layout', 'no layout (grid step)');
            case 'layout_missing': return tx('rpv_skip_missing', 'layout file not found');
            default: return '';
        }
    };

    const summary = () => (
        <Box sx={{ overflowX: 'auto', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                <thead>
                    <tr>
                        {[tx('rpv_col_step', 'Step'), tx('rpv_col_layout', 'Layout'), tx('rpv_col_rows', 'Rows'), ''].map((header, index) => (
                            <th key={index} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid rgba(0,0,0,0.12)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.7 }}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(input?.datasets || []).map((ds) => (
                        <tr key={ds.step}>
                            <td style={{ padding: '6px 10px' }}>{ds.step} ({ds.type})</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'Consolas, monospace' }}>{ds.template || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{ds.rowCount}{ds.truncated ? '+' : ''}</td>
                            <td style={{ padding: '6px 10px', opacity: 0.75 }}>{ds.skipped ? skippedLabel(ds.skipped) : (ds.templateXml ? '✓' : '')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Box>
    );

    return (
        <Dialog open={open} onClose={() => { if (phase !== 'running') onClose && onClose(); }}
            fullWidth maxWidth={phase === 'done' ? 'lg' : 'sm'}>
            <DialogTitle sx={{ fontSize: 16 }}>
                {tx('rpv_title', 'Preview')}{props.title ? ' — ' + props.title : ''}
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {phase === 'ask' && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            {tx('rpv_ask_intro', 'Enter the document number(s). A comma list previews several documents at once.')}
                        </Typography>
                        {(props.recIdSlots || []).map((entry) => (
                            <RecIdInput key={entry.slot}
                                report={props.report || null}
                                slot={entry.slot}
                                lang={props.lang}
                                lookups={props.lookups}
                                label={props.report ? '' : entry.label}
                                value={values[entry.slot] || ''}
                                onChange={(next) => setValues((current) => ({ ...current, [entry.slot]: next }))}
                                t={props.t} />
                        ))}
                        {validation === 'length' && (
                            <Alert severity="warning">{tx('rpv_length_mismatch', 'Lists must have the same length (or a single value).')}</Alert>
                        )}
                    </>
                )}

                {(phase === 'running' || phase === 'rendering') && (
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                {phase === 'running'
                                    ? tx('rpv_running', 'Running the report…') + (progress?.step && progress?.step_count ? ` (${progress.step}/${progress.step_count})` : '')
                                    : tx('rpv_rendering', 'Rendering the layout…')}
                            </Typography>
                        </Stack>
                        <LinearProgress />
                    </Stack>
                )}

                {phase === 'done' && (
                    <>
                        {warnings.length > 0 && (
                            <Alert severity="info">{warnings.join(' · ')}</Alert>
                        )}
                        <Box sx={{ height: '72vh', border: '1px solid rgba(0,0,0,0.12)' }}>
                            <iframe title="preview" src={pdfUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                        </Box>
                    </>
                )}

                {phase === 'unavailable' && (
                    <>
                        <Alert severity="info">
                            {tx('rpv_unavailable', 'The layout preview engine is not available yet. The report ran and the data and layout below are ready for it.')}
                        </Alert>
                        {summary()}
                        {input?.logo && (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <img alt="logo" src={'data:' + input.logo.mime + ';base64,' + input.logo.base64}
                                    style={{ maxWidth: 160, maxHeight: 60, objectFit: 'contain', background: '#fff', border: '1px solid rgba(0,0,0,0.12)' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {tx('rpv_logo', 'Logo')}: {input.logo.kind} · {input.logo.scope}
                                </Typography>
                            </Stack>
                        )}
                    </>
                )}

                {phase === 'error' && (
                    <>
                        <Alert severity="error">{message}</Alert>
                        {input && summary()}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {phase === 'ask' && (
                    <>
                        <Button onClick={onClose}>{tx('cancel', 'Cancel')}</Button>
                        <Button variant="contained" disabled={validation !== '' && slots.length > 0} onClick={start}>
                            {tx('rpv_run', 'Preview')}
                        </Button>
                    </>
                )}
                {phase === 'done' && pdfUrl && (
                    <Button component="a" href={pdfUrl} download={(props.title || 'preview') + '.pdf'}>
                        {tx('rpv_download', 'Download PDF')}
                    </Button>
                )}
                {phase !== 'ask' && phase !== 'running' && (
                    <Button variant="contained" onClick={onClose}>{tx('reports_close', 'Close')}</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ReportPreviewDialog;
